import type { PublicTracking } from "@vaulted/shared";
import { useEffect } from "react";

/**
 * Marketing tags on the public booking pages only (never the staff app), when the clinic has tracking
 * on: Google Analytics and the Facebook pixel record page views and "started booking". The booking's
 * conversion is sent by the server, with the cookies below, when the booking goes live.
 */

type Win = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void; fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; callMethod?: (...a: unknown[]) => void }; _fbq?: unknown };
const w = () => window as Win;
let loaded: string | null = null;

function script(src: string) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

/** Load the tags once per page (the IDs come from the clinic's settings). */
export function useMarketingTags(t: PublicTracking | null | undefined) {
  useEffect(() => {
    const key = t ? `${t.googleMeasurementId}|${t.facebookPixelId}` : null;
    if (!t || !key || loaded === key) return;
    loaded = key;
    if (t.googleMeasurementId) {
      const g = w();
      g.dataLayer = g.dataLayer ?? [];
      // gtag passes its arguments object through, as Google's own snippet does.
      g.gtag = function gtag() {
        // biome-ignore lint/complexity/noArguments: gtag reads the arguments object itself.
        g.dataLayer!.push(arguments);
      };
      g.gtag("js", new Date());
      g.gtag("config", t.googleMeasurementId);
      script(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(t.googleMeasurementId)}`);
    }
    if (t.facebookPixelId) {
      const g = w();
      if (!g.fbq) {
        const fbq = ((...args: unknown[]) => (fbq.callMethod ? fbq.callMethod(...args) : fbq.queue!.push(args))) as NonNullable<Win["fbq"]> & { push?: unknown; loaded?: boolean; version?: string };
        fbq.queue = [];
        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = "2.0";
        g.fbq = fbq;
        g._fbq = fbq;
        script("https://connect.facebook.net/en_US/fbevents.js");
      }
      // No customer details are given to the pixel.
      g.fbq!("init", t.facebookPixelId);
      g.fbq!("track", "PageView");
    }
  }, [t]);
}

/** Someone started booking (picked what they want and reached the times). */
export function trackStartedBooking() {
  w().gtag?.("event", "begin_checkout");
  w().fbq?.("track", "InitiateCheckout");
}

const cookie = (name: string) => document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1];

/** The ad platforms' cookies, sent with the booking (only when tags are on the page). */
export function trackingCookies(t: PublicTracking | null | undefined) {
  if (!t) return undefined;
  const ga = cookie("_ga")?.split(".");
  return {
    // "GA1.1.123.456" → "123.456", Google's client ID.
    gaClientId: ga && ga.length >= 4 ? ga.slice(-2).join(".") : undefined,
    fbp: cookie("_fbp"),
    fbc: cookie("_fbc"),
    url: window.location.href.split("#")[0]!.slice(0, 1000),
  };
}
